/**
 * WidgetCommandPalette
 *
 * Cmd+K (or Ctrl+K) command palette for the report editor.
 * Lets users search across all widget types in all categories and add
 * them to the canvas with a single keyboard-driven flow.
 *
 * Visual design matches the app-level CommandPaletteModal in DashboardLayout —
 * same rounded-3xl modal, same bordered search input, same hover ring rows.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { ACTIVE_CATEGORIES, isPresetType } from "./WidgetPalette";
import type { WidgetType } from "./WidgetPalette";
import type { ValidColSpan } from "../../lib/gridUtils";

export interface WidgetCommandPaletteProps {
  onAddWidget: (
    type: WidgetType,
    colSpan?: ValidColSpan,
    rowSpan?: number,
  ) => void;
}

interface SearchableItem {
  type: WidgetType;
  label: string;
  category: string;
  colSpan: ValidColSpan;
  rowSpan: number;
  Icon: React.ComponentType<{ size?: number; className?: string }>;
}

export function WidgetCommandPalette({ onAddWidget }: WidgetCommandPaletteProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  // Listen for Cmd+K (or Ctrl+K) globally. Use the capture phase + immediate
  // propagation halt so we intercept the keystroke BEFORE the app-level
  // command palette (in DashboardLayout) sees it. Inside the report editor,
  // Cmd+K should open ONLY this palette.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        e.stopImmediatePropagation();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    }
    // Capture: true → runs before any bubbling listener (the app-level one)
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open]);

  // Reset state and focus when opening
  useEffect(() => {
    if (open) {
      setQuery("");
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Build flat searchable list of widget types from active categories
  const allItems = useMemo<SearchableItem[]>(() => {
    const items: SearchableItem[] = [];
    for (const cat of ACTIVE_CATEGORIES) {
      if (!cat.enabled) continue;
      for (const it of cat.items) {
        if (isPresetType(it.type)) continue;
        items.push({
          type: it.type as WidgetType,
          label: it.label,
          category: cat.label,
          colSpan: it.defaultColSpan,
          rowSpan: it.defaultRowSpan,
          Icon: it.icon,
        });
      }
    }
    return items;
  }, []);

  // Recents — pulled from localStorage, capped at 5 most recent widget types
  const RECENTS_KEY = "report-editor-recent-widgets";
  const [recents, setRecents] = useState<WidgetType[]>(() => {
    try {
      const raw = localStorage.getItem(RECENTS_KEY);
      return raw ? (JSON.parse(raw) as WidgetType[]) : [];
    } catch {
      return [];
    }
  });

  const pushRecent = (type: WidgetType) => {
    setRecents((prev) => {
      const next = [type, ...prev.filter((t) => t !== type)].slice(0, 5);
      try {
        localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
      } catch {
        // localStorage might be unavailable (private mode); silently ignore.
      }
      return next;
    });
  };

  const recentItems = useMemo<SearchableItem[]>(() => {
    if (query.trim()) return [];
    const byType = new Map(allItems.map((it) => [it.type, it]));
    return recents
      .map((t) => byType.get(t))
      .filter((it): it is SearchableItem => Boolean(it));
  }, [allItems, recents, query]);

  const otherItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      const recentSet = new Set(recents);
      return allItems.filter((it) => !recentSet.has(it.type)).slice(0, 50);
    }
    return allItems
      .filter(
        (it) =>
          it.label.toLowerCase().includes(q) ||
          it.category.toLowerCase().includes(q) ||
          it.type.toLowerCase().includes(q),
      )
      .slice(0, 50);
  }, [allItems, query, recents]);

  // Flat results list (recents first when no query, then others)
  const results = useMemo(() => [...recentItems, ...otherItems], [recentItems, otherItems]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleSelect = (item: SearchableItem) => {
    pushRecent(item.type);
    onAddWidget(item.type, item.colSpan, item.rowSpan);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && results[selectedIndex]) {
      e.preventDefault();
      handleSelect(results[selectedIndex]!);
    }
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          onClick={() => setOpen(false)}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/35"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          />

          {/* Palette */}
          <motion.div
            className="relative w-full max-w-sm bg-background border border-border rounded-3xl shadow-2xl overflow-hidden"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="widget-command-palette"
          >
            {/* Search input */}
            <div className="flex items-center gap-3 px-4 pt-6 pb-4" style={{ margin: "0 12px" }}>
              <div className="flex-1 flex items-center gap-2 rounded-lg border-[0.5px] border-input px-3 py-2">
                <MagnifyingGlass size={16} className="text-foreground/80 shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search widgets..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1 text-sm text-foreground bg-transparent placeholder:text-foreground/80"
                  style={{ outline: "none", boxShadow: "none", border: "none" }}
                  data-testid="widget-command-palette-input"
                />
              </div>
              <kbd
                className="text-xs text-muted-foreground font-medium border border-input rounded-md px-1.5 py-0.5 bg-muted/40 cursor-pointer hover:bg-muted/60"
                onClick={() => setOpen(false)}
              >
                esc
              </kbd>
            </div>

            {/* Results */}
            <div className="h-[600px] overflow-y-auto py-1">
              {results.length > 0 ? (
                results.map((item, i) => {
                  const Icon = item.Icon;
                  const showRecentLabel = !query.trim() && recentItems.length > 0 && i === 0;
                  const showAllLabel = !query.trim() && recentItems.length > 0 && i === recentItems.length;
                  return (
                    <div key={`${item.category}-${item.type}-${i}`} className="px-3">
                      {showRecentLabel && (
                        <div className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Recent
                        </div>
                      )}
                      {showAllLabel && (
                        <div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          All widgets
                        </div>
                      )}
                      <motion.button
                        type="button"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.03 * (i + 1) }}
                        onClick={() => handleSelect(item)}
                        onMouseEnter={() => {
                          setHoveredIndex(i);
                          setSelectedIndex(i);
                        }}
                        onMouseLeave={() => setHoveredIndex(-1)}
                        data-testid={`command-item-${item.type}`}
                        className={`w-full text-left px-4 py-4 text-sm flex items-center gap-3 cursor-pointer rounded-[14px] border ${
                          i === selectedIndex
                            ? "bg-muted/20 text-foreground border-input shadow-xs"
                            : "text-foreground border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs"
                        }`}
                      >
                        <Icon size={19} className="text-foreground shrink-0" />
                        <span>{item.label}</span>
                        <span className="ml-auto text-[14px] text-muted-foreground">{item.category}</span>
                      </motion.button>
                      {i < results.length - 1 && (
                        <div
                          className="mx-4"
                          style={{
                            borderBottom: "0.5px solid hsl(var(--input) / 0.4)",
                            opacity: hoveredIndex === i || hoveredIndex === i + 1 ? 0 : 1,
                          }}
                        />
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                  No widgets found.
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
